import * as THREE from 'three';
import type { LightingRig } from './types';

export interface LightingShadowConfig {
  readonly enabled?: boolean;
  readonly mapSize?: number;
  readonly cameraSize?: number;
  readonly near?: number;
  readonly far?: number;
  readonly bias?: number;
  readonly normalBias?: number;
}

export interface LightingConfig {
  /** Key light intensity. Default: 1.2. */
  readonly keyIntensity?: number;
  /** Key light color. Default: 0xffffff (neutral white). */
  readonly keyColor?: THREE.ColorRepresentation;
  /** Key light direction (normalized). Default: (-1, -2, -1).normalize(). */
  readonly keyDirection?: THREE.Vector3;
  /** Fill light intensity. Default: 0.4. */
  readonly fillIntensity?: number;
  /** Fill light color. Default: 0xa3ccff (cool fill). */
  readonly fillColor?: THREE.ColorRepresentation;
  /** Hemisphere top color. Default: 0xfff5e1 (warm sky). */
  readonly rimSkyColor?: THREE.ColorRepresentation;
  /** Hemisphere bottom color. Default: 0x303040 (cool ground). */
  readonly rimGroundColor?: THREE.ColorRepresentation;
  /** Hemisphere intensity. Default: 0.3. */
  readonly rimIntensity?: number;
  /** Optional key-light shadow tuning. Disabled by default for legacy maps. */
  readonly shadow?: LightingShadowConfig;
}

const DEFAULT_KEY_DIRECTION = new THREE.Vector3(-1, -2, -1).normalize();
const DEFAULT_SHADOW_CONFIG = Object.freeze({
  enabled: false,
  mapSize: 1024,
  cameraSize: 48,
  near: 0.5,
  far: 120,
  bias: -0.0005,
  normalBias: 0.02,
});

function applyKeyDirection(
  key: THREE.DirectionalLight,
  fill: THREE.DirectionalLight,
  keyDirection: THREE.Vector3,
): void {
  const direction = keyDirection.clone().normalize();
  key.position.set(-direction.x, -direction.y, -direction.z);
  fill.position.set(direction.x, direction.y, direction.z);
}

function applyKeyShadowConfig(key: THREE.DirectionalLight, config?: LightingShadowConfig): void {
  const shadowConfig = {
    ...DEFAULT_SHADOW_CONFIG,
    ...(config ?? {}),
  };
  key.castShadow = shadowConfig.enabled;
  key.shadow.mapSize.set(shadowConfig.mapSize, shadowConfig.mapSize);
  key.shadow.bias = shadowConfig.bias;
  key.shadow.normalBias = shadowConfig.normalBias;

  const camera = key.shadow.camera;
  if (camera instanceof THREE.OrthographicCamera) {
    const halfSize = shadowConfig.cameraSize / 2;
    camera.left = -halfSize;
    camera.right = halfSize;
    camera.top = halfSize;
    camera.bottom = -halfSize;
    camera.near = shadowConfig.near;
    camera.far = shadowConfig.far;
    camera.updateProjectionMatrix();
  }
}

/**
 * Creates a three-light rig (key / fill / rim) and adds all lights to the scene.
 *
 * - key: DirectionalLight — main source, warm-neutral, casts primary shadows
 * - fill: DirectionalLight — secondary source, cool fill from the opposite side
 * - rim: HemisphereLight — ambient sky/ground separation for depth
 *
 * Returns a `LightingRig` with `dispose()` that removes the lights from the
 * scene idempotently.
 */
export function createLightingRig(scene: THREE.Scene, config?: LightingConfig): LightingRig {
  const {
    keyIntensity = 1.2,
    keyColor = 0xffffff,
    keyDirection = DEFAULT_KEY_DIRECTION,
    fillIntensity = 0.4,
    fillColor = 0xa3ccff,
    rimSkyColor = 0xfff5e1,
    rimGroundColor = 0x303040,
    rimIntensity = 0.3,
    shadow,
  } = config ?? {};

  // Key light — primary directional light, positioned via the direction vector
  const key = new THREE.DirectionalLight(keyColor, keyIntensity);

  // Fill light — counter-key, opposing direction, softer intensity
  const fill = new THREE.DirectionalLight(fillColor, fillIntensity);
  applyKeyDirection(key, fill, keyDirection);
  applyKeyShadowConfig(key, shadow);

  // Rim — hemisphere for sky/ground ambient separation
  const rim = new THREE.HemisphereLight(rimSkyColor, rimGroundColor, rimIntensity);

  scene.add(key, fill, rim);

  let disposed = false;

  return {
    key,
    fill,
    rim,
    dispose() {
      if (disposed) return;
      disposed = true;
      scene.remove(key, fill, rim);
    },
  };
}

export function applyLightingConfig(rig: LightingRig, config: LightingConfig): void {
  if (config.keyIntensity != null) rig.key.intensity = config.keyIntensity;
  if (config.keyColor != null) rig.key.color.set(config.keyColor);
  if (config.fillIntensity != null) rig.fill.intensity = config.fillIntensity;
  if (config.fillColor != null) rig.fill.color.set(config.fillColor);
  if (config.rimIntensity != null) rig.rim.intensity = config.rimIntensity;
  if (config.rimSkyColor != null) rig.rim.color.set(config.rimSkyColor);
  if (config.rimGroundColor != null) rig.rim.groundColor.set(config.rimGroundColor);

  if (config.keyDirection != null) {
    applyKeyDirection(rig.key, rig.fill, config.keyDirection);
  }
  if (config.shadow != null) applyKeyShadowConfig(rig.key, config.shadow);
}
